import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, ImageIcon, Loader2 } from 'lucide-react';

import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePurchase } from '../hooks/usePurchase';
import { usePlayPrices } from '../hooks/usePlayPrices';
import { runPurchaseWithRetry } from '../utils/purchaseRetry';
import { track } from '../utils/analytics';
import { Button } from '../components/ui/button';
import PremiumModal from '../components/PremiumModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEVEL_NAMES = {
  fr: { 1: 'Découverte', 2: 'Connaissance', 3: 'Amitié', 4: 'Rapprochement', 5: 'Romance' },
  en: { 1: 'Discovery',  2: 'Acquaintance', 3: 'Friendship', 4: 'Getting Closer', 5: 'Romance' },
  es: { 1: 'Descubrimiento', 2: 'Conocimiento', 3: 'Amistad', 4: 'Acercamiento', 5: 'Romance' },
  de: { 1: 'Entdeckung', 2: 'Bekanntschaft', 3: 'Freundschaft', 4: 'Annäherung', 5: 'Romanze' },
  pt: { 1: 'Descoberta', 2: 'Conhecimento', 3: 'Amizade', 4: 'Aproximação', 5: 'Romance' },
};

const PAGE_TITLE = {
  fr: 'Débloquer',
  en: 'Unlock',
  es: 'Desbloquear',
  de: 'Freischalten',
  pt: 'Desbloquear',
};

export default function MonetizationPage() {
  const navigate = useNavigate();
  const { gender, characterId } = useParams();
  const { token, isSubscribed, getCharacterLevels } = useAuth();
  const { language } = useLanguage();
  const playPrices = usePlayPrices();
  const { startPurchase: _basePurchase } = usePurchase();
  const startPurchase = useCallback(
    (params) => runPurchaseWithRetry({ startPurchase: _basePurchase, params, toast, language }),
    [_basePurchase, language]
  );

  const [characterName, setCharacterName] = useState('');
  const [photosUnlocked, setPhotosUnlocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [activating, setActivating] = useState(false);
  const [purchasingLevel, setPurchasingLevel] = useState(false);
  const [purchasingPhotos, setPurchasingPhotos] = useState(false);

  const unlockedLevels = getCharacterLevels(characterId);
  const maxUnlockedLevel = useMemo(
    () => Math.max(...(unlockedLevels || [1])),
    [unlockedLevels]
  );
  const nextLevel = maxUnlockedLevel + 1;
  const nextLevelName = nextLevel <= 5
    ? (LEVEL_NAMES[language] || LEVEL_NAMES.fr)[nextLevel]
    : null;

  // Where to return after the user is done with this page.
  const backPath = `/chat/${gender}/${characterId}/${maxUnlockedLevel}`;

  // Load character name + existing photos-unlocked state.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [charRes, convRes] = await Promise.all([
          axios.get(`${API}/characters/${gender}?language=${language}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API}/chat/character/${characterId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        if (!active) return;
        const c = (charRes.data || []).find((ch) => ch.id === characterId);
        if (c) setCharacterName(c.name || characterId);
        const msgs = convRes.data?.conversation?.messages || [];
        const hasLockedPhoto = msgs.some((m) => m.photo_locked);
        const hasAnyPhoto = msgs.some((m) => m.image_url || m.video_url);
        setPhotosUnlocked(!hasLockedPhoto && hasAnyPhoto);
      } catch (err) {
        console.error('MonetizationPage load error:', err);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [gender, characterId, token, language]);

  const handleLevelPurchase = async () => {
    if (nextLevel > 5) return;
    setPurchasingLevel(true);
    try {
      const res = await startPurchase({
        itemType: 'level',
        itemId: `${characterId}:${nextLevel}`,
        returnTo: `/chat/${gender}/${characterId}/${nextLevel}`,
        onPopupClosed: () => setActivating(true),
      });
      if (res.redirected) return;
      if (res.success) {
        track('one_time_purchase_completed', { type: 'level', character_id: characterId });
        navigate(`/chat/${gender}/${characterId}/${nextLevel}`);
      } else if (!res.cancelled) {
        toast.error(res.message || 'Erreur');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setPurchasingLevel(false);
      setActivating(false);
    }
  };

  const handleUnlockPhotos = async () => {
    setPurchasingPhotos(true);
    try {
      const res = await startPurchase({
        itemType: 'photos',
        itemId: characterId,
        returnTo: backPath,
        onPopupClosed: () => setActivating(true),
      });
      if (res.redirected) return;
      if (res.success) {
        track('one_time_purchase_completed', { type: 'photos', character_id: characterId });
        setPhotosUnlocked(true);
        navigate(backPath);
      } else if (!res.cancelled) {
        toast.error(res.message || 'Erreur');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setPurchasingPhotos(false);
      setActivating(false);
    }
  };

  const handleSubscribe = async () => {
    try {
      const res = await startPurchase({
        itemType: 'subscription',
        itemId: 'premium_monthly',
        returnTo: backPath,
        onPopupClosed: () => {
          setShowPremiumModal(false);
          setActivating(true);
        },
      });
      if (res.redirected) return;
      setShowPremiumModal(false);
      if (res.success) {
        track('subscription_completed');
        navigate(backPath);
      } else if (!res.cancelled) {
        toast.error(res.message || 'Erreur');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setActivating(false);
    }
  };

  return (
    <div className="min-h-screen bg-velvet-bg text-velvet_text-primary" data-testid="monetization-page">
      {/* Header */}
      <header className="glass border-b border-white/5 px-4 py-3 flex items-center gap-4 sticky top-0 z-10">
        <button
          onClick={() => navigate(backPath)}
          className="text-velvet_text-secondary hover:text-velvet_text-accent transition-colors"
          data-testid="back-from-monetization"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="font-heading text-xl">
          {PAGE_TITLE[language] || PAGE_TITLE.en}
        </h1>
      </header>

      <main className="max-w-md mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-purple-light" size={28} />
          </div>
        ) : (
          <div className="glass rounded-xl p-4 space-y-3" data-testid="monetization-purchase-section">
            {!isSubscribed && (
              <Button
                onClick={() => setShowPremiumModal(true)}
                className="w-full bg-gradient-to-r from-purple-main to-fuchsia-main hover:opacity-90 text-white rounded-full text-sm h-auto py-2 flex flex-col items-center"
                data-testid="monetization-subscribe-button"
              >
                <span className="font-semibold">
                  {({fr: 'Abonnement Premium', en: 'Premium subscription', es: 'Suscripción Premium', de: 'Premium-Abonnement', pt: 'Assinatura Premium'})[language] || 'Premium subscription'}
                </span>
                <span className="text-xs mt-0.5">
                  {playPrices.subscription.full && (
                    <span className="line-through opacity-70 mr-1">{playPrices.subscription.full}</span>
                  )}
                  {playPrices.subscription.promo}
                </span>
              </Button>
            )}

            {nextLevelName && (
              <Button
                onClick={handleLevelPurchase}
                disabled={purchasingLevel}
                variant="outline"
                className="w-full border-white/20 text-velvet_text-primary hover:bg-white/10 rounded-full text-sm h-auto py-2 flex flex-col items-center"
                data-testid="monetization-next-level-button"
              >
                <span className="font-semibold">
                  {language === 'fr' ? `Niveau "${nextLevelName}"` : `Level "${nextLevelName}"`}
                </span>
                <span className="text-xs mt-0.5">
                  <span className="line-through opacity-60 mr-1">{language === 'fr' ? '2,99 €' : '€2.99'}</span>
                  {language === 'fr' ? '1,49 €' : '€1.49'}
                </span>
              </Button>
            )}

            {!photosUnlocked && (
              <Button
                onClick={handleUnlockPhotos}
                disabled={purchasingPhotos}
                variant="outline"
                className="w-full border-white/20 text-velvet_text-primary hover:bg-white/10 rounded-full text-sm h-auto py-2 flex flex-col items-center"
                data-testid="monetization-unlock-photos-button"
              >
                <span className="font-semibold flex items-center gap-1">
                  <ImageIcon size={14} />
                  {language === 'fr'
                    ? `Toutes les photos de ${characterName}`
                    : `All of ${characterName}'s photos`}
                </span>
                <span className="text-xs mt-0.5 text-velvet_text-secondary">
                  {language === 'fr'
                    ? 'Accès illimité aux photos actuelles et futures'
                    : 'Unlimited access to current and future photos'}
                </span>
                <span className="text-xs mt-0.5">
                  <span className="line-through opacity-60 mr-1">{language === 'fr' ? '2,99 €' : '€2.99'}</span>
                  {language === 'fr' ? '1,49 €' : '€1.49'}
                </span>
              </Button>
            )}

            {/* When everything is already unlocked, give a friendly heads-up */}
            {isSubscribed && !nextLevelName && photosUnlocked && (
              <p className="text-center text-sm text-velvet_text-secondary py-3" data-testid="monetization-all-unlocked">
                {({
                  fr: 'Tout est débloqué — profite ✨',
                  en: 'Everything is unlocked — enjoy ✨',
                  es: 'Todo está desbloqueado — disfruta ✨',
                  de: 'Alles ist freigeschaltet — viel Spaß ✨',
                  pt: 'Tudo desbloqueado — aproveita ✨',
                })[language] || 'Everything is unlocked — enjoy ✨'}
              </p>
            )}
          </div>
        )}
      </main>

      {/* Premium modal (same as ChatPage) */}
      <PremiumModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        lang={language}
        onSubscribe={handleSubscribe}
      />

      {/* Activation loader */}
      {activating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-velvet-bg/90 backdrop-blur">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="animate-spin text-purple-light" size={32} />
            <p className="text-velvet_text-primary text-sm">
              {({fr: 'Activation en cours…', en: 'Activating…', es: 'Activando…', de: 'Wird aktiviert…', pt: 'A ativar…'})[language] || 'Activating…'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
